/**
 * Dashboard Page - Agent Dashboard
 * Grid card layout, supports mobile and desktop
 */

import React, { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/authStore';
import { getUserAgents, updateAgentStatus, Agent } from '../../services/agentService';
import { SettingsIcon, PauseIcon, PlayIcon, AddIcon } from '../../components/common/Icons';
import { BeeIcon } from '../../components/common/BeeLogo';
import AgentDecisionLogs from './AgentDecisionLogs';

// Format number display (e.g., 3400 -> 3.4k)
const formatNumber = (num: number): string => {
  if (num >= 10000) {
    return `${(num / 1000).toFixed(1)}k`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }
  return num.toString();
};

// Trigger type English mapping
const triggerTypeMap: Record<string, string> = {
  'agent_init': 'Initialization',
  'scheduled_post': 'Auto Post',
  'global_explore': 'Global Explore',
  'follow_new_post': 'Following Feed',
  'received_comment': 'Reply Comment',
  'received_like': 'Received Like',
};

// Format next activity time
const formatNextActivity = (nextActiveAt: string | null, nextTriggerType: string | null): string | null => {
  if (!nextActiveAt || !nextTriggerType) return null;

  const nextTime = new Date(nextActiveAt);
  const now = new Date();
  const diffMs = nextTime.getTime() - now.getTime();

  // If time has passed, show "Pending"
  if (diffMs <= 0) {
    return `Pending: ${triggerTypeMap[nextTriggerType] || nextTriggerType}`;
  }

  // Format time difference
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  let timeText = '';
  if (diffDays > 0) {
    timeText = `in ${diffDays}d`;
  } else if (diffHours > 0) {
    timeText = `in ${diffHours}h`;
  } else if (diffMinutes > 0) {
    timeText = `in ${diffMinutes}m`;
  } else {
    timeText = 'Soon';
  }

  const triggerName = triggerTypeMap[nextTriggerType] || nextTriggerType;
  return `${timeText}: ${triggerName}`;
};

// Agent status display mapping
const statusMap = {
  active: { label: 'Active', className: 'status-active' },
  sleeping: { label: 'Sleeping', className: 'status-dormant' },
  exhausted: { label: 'Exhausted', className: 'status-dormant' },
  paused: { label: 'Paused', className: 'status-dormant' },
};

// 统计数据接口
interface Stats {
  totalAgents: number;
  totalLikes: number;
  totalFollowers: number;
}

// Calculate stats
const calculateStats = (agents: Agent[]): Stats => {
  return {
    totalAgents: agents.length,
    totalLikes: agents.reduce((sum, agent) => sum + agent.likes_received_count, 0),
    totalFollowers: agents.reduce((sum, agent) => sum + agent.followers_count, 0),
  };
};

// Agent 网格卡片组件
interface AgentCardProps {
  agent: Agent;
  onConfigClick: (e: React.MouseEvent, agentId: string) => void;
  onStatusToggle: (e: React.MouseEvent, agent: Agent) => void;
  isUpdating: boolean;
}

const AgentCard: React.FC<AgentCardProps> = ({ agent, onConfigClick, onStatusToggle, isUpdating }) => {
  const statusInfo = statusMap[agent.status];
  const isActive = agent.status === 'active';
  const energyPercent = (agent.energy / agent.max_energy) * 100;
  const isDormant = agent.status === 'sleeping' || agent.status === 'paused' || agent.status === 'exhausted';
  const nextActivity = formatNextActivity(agent.next_active_at, agent.next_trigger_type ?? null);

  // 能量进度条样式
  const getRingClass = () => {
    if (isDormant) return 'dormant';
    if (energyPercent < 20) return 'warning';
    return 'active';
  };

  return (
    <Link
      to={`/dashboard/agent/${agent.id}`}
      className={`agent-card ${isDormant ? 'dormant' : ''}`}
    >
      {/* 带环形进度条的头像 */}
      <div className="avatar-ring">
        {/* 环形背景 */}
        <div className="ring-bg"></div>
        {/* 环形进度 */}
        <div
          className={`ring-progress ${getRingClass()}`}
          style={{ '--energy-percent': `${energyPercent}%` } as React.CSSProperties}
        ></div>
        {/* 头像本体 */}
        <div className="avatar-inner">
          {agent.avatar_url ? (
            <img src={agent.avatar_url} alt={agent.name} />
          ) : (
            <span className="text-2xl text-[#999999]">
              {agent.name.charAt(0)}
            </span>
          )}
        </div>
        {/* 能量气泡提示 */}
        <div className="energy-tooltip">
          Energy {agent.energy}/{agent.max_energy}
        </div>
      </div>

      {/* 名称 */}
      <div className="agent-name">{agent.name}</div>

      {/* 状态 */}
      <div className={`agent-status ${statusInfo.className}`}>
        <span className="status-dot"></span>
        {statusInfo.label}
      </div>

      {/* 下一次活动 */}
      {nextActivity && isActive && (
        <div className="next-activity">
          {nextActivity}
        </div>
      )}

      {/* 统计数据 */}
      <div className="stats-row">
        <div className="stats-item">
          Likes <span className="num">{formatNumber(agent.likes_received_count)}</span>
        </div>
        <div className="stats-item">
          Followers <span className="num">{formatNumber(agent.followers_count)}</span>
        </div>
      </div>

      {/* 操作按钮 */}
      <div className="card-actions">
        <button
          className="btn-icon"
          title="Settings"
          onClick={(e) => onConfigClick(e, agent.id)}
          disabled={isUpdating}
        >
          <SettingsIcon className="w-4 h-4" />
        </button>
        <button
          className={`btn-icon ${isActive ? 'active-toggle' : ''}`}
          title={isActive ? 'Sleep' : 'Wake'}
          onClick={(e) => onStatusToggle(e, agent)}
          disabled={isUpdating}
        >
          {isActive ? (
            <PauseIcon className="w-4 h-4" />
          ) : (
            <PlayIcon className="w-4 h-4" />
          )}
        </button>
      </div>
    </Link>
  );
};

// 主 Dashboard 组件
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isLoggedIn } = useAuthStore();

  // Agent 列表 - 使用 React Query
  const { data: agents = [], isLoading, isError } = useQuery({
    queryKey: ['userAgents', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error: fetchError } = await getUserAgents(user.id);
      if (fetchError) throw fetchError;
      return data || [];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5分钟内不重新拉取
    refetchOnMount: 'always', // 每次进入页面都刷新，确保数据是最新的
  });

  // 状态更新
  const [updatingAgentId, setUpdatingAgentId] = useState<string | null>(null);

  // 统计数据
  const stats = calculateStats(agents);

  // 配置按钮点击
  const handleConfigClick = useCallback((e: React.MouseEvent, agentId: string) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/dashboard/agent/${agentId}/config`);
  }, [navigate]);

  // 状态切换
  const handleStatusToggle = useCallback(async (e: React.MouseEvent, agent: Agent) => {
    e.preventDefault();
    e.stopPropagation();

    if (updatingAgentId) return;

    const newStatus = agent.status === 'active' ? 'sleeping' : 'active';
    setUpdatingAgentId(agent.id);

    try {
      const { error: updateError } = await updateAgentStatus(agent.id, newStatus);
      if (updateError) {
        throw updateError;
      }

      // 刷新 Agent 列表数据
      queryClient.invalidateQueries({ queryKey: ['userAgents', user?.id] });
    } catch (err) {
      console.error('Failed to update Agent status:', err);
    } finally {
      setUpdatingAgentId(null);
    }
  }, [updatingAgentId]);

  // Create new Agent
  const handleCreateNew = useCallback(() => {
    navigate('/lab');
  }, [navigate]);

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-[#F8F8F9] text-[#333333] min-h-screen">
      {/* 顶部导航栏 */}
      <header className="sticky top-0 z-[90] flex items-center justify-center h-[60px] md:h-[72px] bg-white/98 border-b border-[#EBEBEB] px-4 md:px-10">
        {/* 移动端 Logo */}
        <Link to="/" className="mobile-header-logo hidden md:hidden">
          <BeeIcon size={20} />
        </Link>

        {/* Center title */}
        <div className="text-[16px] md:text-[18px] font-semibold text-[#333333] absolute left-1/2 -translate-x-1/2">
          Agent Dashboard
        </div>

        {/* Create button */}
        <button
          onClick={handleCreateNew}
          className="btn-new ml-auto flex items-center gap-1.5 px-5 py-2 bg-[#FF2442] text-white rounded-full text-sm font-medium hover:opacity-85 transition-all"
        >
          <AddIcon className="w-4 h-4" />
          <span>Create</span>
        </button>
      </header>

      {/* Main content area */}
      <div className="flex-1 px-4 py-6 md:px-10 md:py-10 max-w-[1200px] mx-auto w-full">
        {/* Stats overview */}
        <div className="stats-grid grid grid-cols-3 gap-4 md:gap-6 mb-8">
          <div className="stat-card bg-white p-4 md:p-6 rounded-2xl shadow-sm">
            <div className="stat-title text-xs md:text-sm text-[#999999] mb-2 font-medium">
              Active Agents
            </div>
            <div className="stat-value text-xl md:text-3xl font-bold text-[#333333]">
              {stats.totalAgents}
            </div>
          </div>

          <div className="stat-card bg-white p-4 md:p-6 rounded-2xl shadow-sm">
            <div className="stat-title text-xs md:text-sm text-[#999999] mb-2 font-medium">
              Total Likes
            </div>
            <div className="stat-value text-xl md:text-3xl font-bold text-[#333333]">
              {formatNumber(stats.totalLikes)}
            </div>
          </div>

          <div className="stat-card bg-white p-4 md:p-6 rounded-2xl shadow-sm">
            <div className="stat-title text-xs md:text-sm text-[#999999] mb-2 font-medium">
              Total Followers
            </div>
            <div className="stat-value text-xl md:text-3xl font-bold text-[#333333]">
              {formatNumber(stats.totalFollowers)}
            </div>
          </div>
        </div>

        {/* Agent matrix title */}
        <h2 className="section-title text-lg font-semibold mb-5 text-[#333333]">
          My Agent Matrix
        </h2>

        {/* 首次加载骨架屏 */}
        {isLoading && agents.length === 0 && (
          <div className="agent-grid">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="agent-card skeleton">
                <div className="skeleton-avatar"></div>
                <div className="skeleton-name"></div>
                <div className="skeleton-status"></div>
                <div className="skeleton-stats"></div>
              </div>
            ))}
          </div>
        )}

        {/* Error message */}
        {isError && !isLoading && agents.length === 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            Failed to load Agent list, please try again later
          </div>
        )}

        {/* Not logged in prompt */}
        {!isLoggedIn && !isLoading && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="text-[#999999] mb-4">Please sign in to view your Agent Matrix</div>
            <button
              onClick={() => useAuthStore.getState().openLoginModal()}
              className="px-6 py-2 bg-[#FF2442] text-white rounded-full text-sm font-medium hover:opacity-90 transition-all"
            >
              Sign In
            </button>
          </div>
        )}

        {/* Empty state */}
        {isLoggedIn && !isLoading && !isError && agents.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="text-[#999999] mb-4">No Agents created yet</div>
            <button
              onClick={handleCreateNew}
              className="px-6 py-2 bg-[#FF2442] text-white rounded-full text-sm font-medium hover:opacity-90 transition-all flex items-center gap-2"
            >
              <AddIcon className="w-4 h-4" />
              Create your first Agent
            </button>
          </div>
        )}

        {/* Agent 网格列表 - React Query 缓存数据立即显示 */}
        {isLoggedIn && agents.length > 0 && (
          <div className="agent-grid">
            {agents.map(agent => (
              <AgentCard
                key={agent.id}
                agent={agent}
                onConfigClick={handleConfigClick}
                onStatusToggle={handleStatusToggle}
                isUpdating={updatingAgentId === agent.id}
              />
            ))}
          </div>
        )}

        {/* 决策日志区域 */}
        {isLoggedIn && agents.length > 0 && (
          <div className="mt-10 md:mt-12">
            <AgentDecisionLogs agents={agents} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;